import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useViewerStore } from '../store/viewerStore';
import type { StreamedGameState, GameLogEntry, GameEvent, GameFullPayload } from '../types';

interface ServerToClientEvents {
  'game:state': (state: StreamedGameState) => void;
  'game:event': (event: GameEvent) => void;
  'game:log': (entry: GameLogEntry) => void;
  'game:ended': () => void;
  'game:full': (payload: GameFullPayload) => void;
  'connection:count': (count: number) => void;
  'no:active:game': () => void;
}

interface ClientToServerEvents {
  'viewer:watch': () => void;
  'viewer:join': (gameId: string) => void;
  'viewer:leave': () => void;
}

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export function useGameSocket(gameId?: string) {
  const socketRef = useRef<GameSocket | null>(null);
  const {
    setConnectionStatus,
    setViewerCount,
    setGameState,
    addLogEntry,
    setGameEnded,
    setNoActiveGame,
    setGameFull,
    reset,
  } = useViewerStore();

  const connect = useCallback(() => {
    // Determine socket URL
    const socketUrl = import.meta.env.VITE_SOCKET_URL ||
      (window.location.protocol === 'https:'
        ? `wss://${window.location.hostname}:3001`
        : `ws://${window.location.hostname}:3001`);

    console.log(`[Socket] Connecting to ${socketUrl}`);
    setConnectionStatus('connecting');

    const socket: GameSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnectionStatus('connected');
      if (gameId) {
        // Join specific game by ID
        console.log(`[Socket] Connected, joining game: ${gameId}`);
        socket.emit('viewer:join', gameId);
      } else {
        // Request to watch whatever game is active (most recent)
        console.log('[Socket] Connected, requesting active game');
        socket.emit('viewer:watch');
      }
    });

    socket.on('disconnect', (reason) => {
      console.log(`[Socket] Disconnected: ${reason}`);
      setConnectionStatus('disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error);
      setConnectionStatus('error');
    });

    socket.on('game:state', (state) => {
      console.log('[Socket] Received game state');
      setGameState(state);
    });

    socket.on('game:log', (entry) => {
      console.log('[Socket] Received log entry:', entry.description);
      addLogEntry(entry);
    });

    socket.on('game:event', (event) => {
      console.log('[Socket] Received event:', event.type);
      // Events are typically followed by a state update
    });

    socket.on('game:ended', () => {
      console.log('[Socket] Game ended');
      setGameEnded(true);
    });

    socket.on('no:active:game', () => {
      console.log('[Socket] No active game available');
      setNoActiveGame(true);
    });

    socket.on('game:full', (payload) => {
      console.log('[Socket] Game at capacity:', payload);
      setGameFull(payload);
    });

    socket.on('connection:count', (count) => {
      console.log('[Socket] Viewer count:', count);
      setViewerCount(count);
    });

    return socket;
  }, [gameId, setConnectionStatus, setViewerCount, setGameState, addLogEntry, setGameEnded, setNoActiveGame, setGameFull]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('viewer:leave');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    reset();
  }, [reset]);

  const retry = useCallback(() => {
    if (socketRef.current?.connected) {
      // Already connected, just request game again
      if (gameId) {
        socketRef.current.emit('viewer:join', gameId);
      } else {
        socketRef.current.emit('viewer:watch');
      }
      useViewerStore.getState().setNoActiveGame(false);
      useViewerStore.getState().setGameFull(null);
    } else {
      // Reconnect
      disconnect();
      connect();
    }
  }, [connect, disconnect, gameId]);

  useEffect(() => {
    const socket = connect();

    return () => {
      if (socket) {
        socket.emit('viewer:leave');
        socket.disconnect();
      }
    };
  }, [connect]);

  return { disconnect, retry };
}
